import { Box, Stack } from '@mui/material'
import { UserV1UserServiceowner } from 'c1gen/roles'
import { C1DisplayNameSkeleton } from 'components/core/display-name-details'
import { C1DrawerLink } from 'components/core/drawer_link'
import { C1EmptyIndicator } from 'components/core/empty-indicator/C1EmptyIndicator'
import { MinimalAPIError } from 'components/core/errors/api'
import { useHasBuiltinRole, useHasRole } from 'components/no-ui/capability'
import { AppUserRolesTable } from 'components/pages/app/users/roles/AppUserRolesTable'
import { C1EntitlementDisplayName } from 'components/pages/entitlement/display-name/C1EntitlementDisplayName'
import { AppResourceTypeTraits } from 'lib/constants/app'
import { RoleID } from 'lib/constants/system'
import { SearchAppEntitlementsForAppUserRequest } from 'pbts/c1/api/app/v1/app_entitlement'
import { FC } from 'react'
import { FormattedMessage } from 'react-intl'
import { searchAppEntitlementsForAppUserApi } from 'redux_toolkit/entitlements'

type AppUserRolesWithFetchProps = {
    appUserId: string
    appId: string
}

const pageSize = 20

export const AppUserRolesWithFetch: FC<AppUserRolesWithFetchProps> = ({
    appUserId,
    appId,
}) => {
    const isReadOnlySuperAdmin = useHasBuiltinRole(RoleID.ReadOnlySuperAdmin)
    const isOwner = useHasRole(UserV1UserServiceowner)
    const canNavigateToApp = isReadOnlySuperAdmin || isOwner

    const {
        data,
        error,
        isLoading,
        isError,
    } = searchAppEntitlementsForAppUserApi.useSearchAppEntitlementsForAppUser(
        SearchAppEntitlementsForAppUserRequest.create({
            appUserId,
            appId,
            resourceTypeTraitIds: [AppResourceTypeTraits.role],
            expandMask: { paths: ['*'] },
        }),
        {
            actionable: !!appUserId && !!appId,
            pageSize,
        },
    )

    if (isLoading) return <C1DisplayNameSkeleton />

    if ((isError && error) || !data) {
        return <MinimalAPIError
            err={<FormattedMessage
                id="component_api_error_app_user_roles"
                defaultMessage="Error fetching app user roles"
            />}
        />
    }

    const roles = data.flat()

    if (roles.length === 0) return <C1EmptyIndicator />

    return <Stack direction="row" alignItems="center" spacing={2}>
        <C1EntitlementDisplayName
            entitlement={roles[0]}
            excludeDetails
            readOnly={!canNavigateToApp}
        />
        {
            roles.length > 1 && (
                <C1DrawerLink
                    variant="md"
                    label={<Box sx={{ whiteSpace: 'nowrap' }}>
                        <FormattedMessage
                            defaultMessage="+{count}"
                            id="component_app_user_roles_more"
                            values={{ count: roles.length - 1 }}
                        />
                    </Box>}
                    drawerContent={<AppUserRolesTable appId={appId} appUserId={appUserId} />}
                />
            )
        }
    </Stack>
}
